import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import L, { type LatLngBoundsExpression } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { AppLayout } from '@/components/ui/AppLayout';
import { Input, Label } from '@/components/ui/Input';
import { Badge } from '@/components/ui/StatusBadge';
import { Banner } from '@/components/ui/Banner';
import { Button } from '@/components/ui/Button';
import { MultiSelectChips } from '@/components/ui/MultiSelectChips';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';
import { mapApi, type MapFilters, type MapMarker } from '@/services/map.api';
import { apporteursApi, type ApporteurMapMarker } from '@/services/apporteurs.api';
import { configApi, type ContractType } from '@/services/config.api';
import { getAccentHex } from '@/lib/accent';

type ViewMode = 'prospects' | 'apporteurs' | 'both';

/**
 * Page Carte - vue géographique des prospects (étape 15).
 *
 * Stack : Leaflet + react-leaflet + tuiles OpenStreetMap (pas de clé API,
 * gratuit). Marqueurs colorés par statut (froid/chaud/client). Clic →
 * popup avec résumé + lien vers la fiche.
 *
 * Clusters : désactivés en V1 pour éviter une dépendance de plus
 * (react-leaflet-cluster n'est pas encore compatible React 18). Au-delà de
 * ~500 prospects affichés Leaflet devient lent ; on compense avec le cap
 * serveur à 1000 markers et un message utilisateur l'invitant à filtrer.
 *
 * France metro centroid (46.5, 2.5) + zoom initial 6 - fitBounds est appliqué
 * dès qu'on a ≥ 1 marker pour resserrer l'affichage sur la zone réelle.
 */

const FRANCE_CENTER: [number, number] = [46.5, 2.5];
const FRANCE_ZOOM = 6;

const STATUS_COLORS: Record<MapMarker['status'], string> = {
  cold: '#64748b', // gris (slate-500)
  warm: '#f59e0b', // orange (amber-500)
  client: '#16a34a', // vert (green-600)
};

const STATUS_LABELS: Record<MapMarker['status'], string> = {
  cold: 'Froid',
  warm: 'Chaud',
  client: 'Client',
};

// Patch : les icônes Leaflet utilisent par défaut une URL relative que Vite ne
// résout pas. On crée des `divIcon` SVG simples à la place - zéro asset externe
// à servir. Les particuliers ont un pin arrondi (pas de pointe) pour distinguer
// visuellement un bureau d'entreprise (pin classique) d'une résidence personnelle.
/**
 * Icône spécifique aux apporteurs d'affaires : étoile (5 branches) en
 * couleur accent du projet (bleu Stripe `var(--accent-hex)` par défaut,
 * dynamique selon `useBrandBootstrap`). Couleur alignée sur les boutons
 * filtres de la sidebar Map pour cohérence visuelle - décision UX
 * 2026-04-27 (le `#F5A524` doré était trop pâle sur fond clair).
 */
function apporteurIcon(): L.DivIcon {
  // `getAccentHex()` lit `--accent-hex` du :root (posé par useBrandBootstrap
  // au boot). Repli `#3E63DD` (bleu Stripe par défaut) si la var n'existe
  // pas encore (cas de bootstrap pas terminé).
  const accent = getAccentHex();
  return L.divIcon({
    className: 'map-marker-pin',
    html: `
      <span style="
        display:inline-block;
        width:24px;height:24px;
        background:${accent};
        border:2px solid #fff;
        clip-path: polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%);
        box-shadow:0 2px 4px rgba(0,0,0,0.25);
      "></span>
    `,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -10],
  });
}

function statusIcon(marker: Pick<MapMarker, 'status' | 'type'>): L.DivIcon {
  const color = STATUS_COLORS[marker.status];
  const isIndividual = marker.type === 'individual';
  return L.divIcon({
    className: 'map-marker-pin',
    html: `
      <span style="
        display:inline-block;
        width:20px;height:20px;
        background:${color};
        border:2px solid #fff;
        border-radius:${isIndividual ? '50%' : '50% 50% 50% 0'};
        transform:${isIndividual ? 'none' : 'rotate(-45deg)'};
        box-shadow:0 2px 4px rgba(0,0,0,0.25);
      "></span>
    `,
    iconSize: [20, 20],
    iconAnchor: [10, isIndividual ? 10 : 20],
    popupAnchor: [0, isIndividual ? -12 : -18],
  });
}

export function MapPage() {
  const token = useAuthStore((s) => s.accessToken)!;
  // viewMode pilote QUI s'affiche (prospects/apporteurs/both). Default
  // « prospects » (aligné sur l'usage historique de la page).
  const [viewMode, setViewMode] = useState<ViewMode>('prospects');
  const [filters, setFilters] = useState<MapFilters>({});
  // Rayon géographique : input ville/CP local + km local. Au clic
  // « Appliquer » ils sont propagés dans `filters.near_location` /
  // `filters.radius_km`. Évite un re-fetch à chaque keystroke.
  const [nearLocationInput, setNearLocationInput] = useState('');
  const [radiusKmInput, setRadiusKmInput] = useState('');
  const [data, setData] = useState<{
    markers: MapMarker[];
    totalCount: number;
    truncated: boolean;
    geocodedCenter: { latitude: number; longitude: number } | null;
  } | null>(null);
  const [apporteurMarkers, setApporteurMarkers] = useState<ApporteurMapMarker[]>([]);
  const [apporteurCenter, setApporteurCenter] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [contractTypes, setContractTypes] = useState<ContractType[]>([]);

  useEffect(() => {
    void (async () => {
      try {
        const cts = await configApi.listContractTypes(token);
        setContractTypes(cts.filter((c) => c.isActive));
      } catch {
        setContractTypes([]);
      }
    })();
  }, [token]);

  // Fetch prospects : si viewMode='apporteurs', on skip et on vide.
  useEffect(() => {
    if (viewMode === 'apporteurs') {
      setData({ markers: [], totalCount: 0, truncated: false, geocodedCenter: null });
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    mapApi
      .list(token, filters)
      .then(setData)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Erreur'))
      .finally(() => setLoading(false));
  }, [token, filters, viewMode]);

  // Fetch apporteurs : si viewMode='prospects', on skip et on vide. Le
  // rayon géo (near_location/radius_km) s'applique aux apporteurs aussi
  // pour cohérence quand le mode = 'both' ou 'apporteurs'. Le geocodedCenter
  // retourné est conservé pour le zoom (cf FitBoundsToMarkers).
  useEffect(() => {
    if (viewMode === 'prospects') {
      setApporteurMarkers([]);
      setApporteurCenter(null);
      return;
    }
    apporteursApi
      .map(token, {
        near_location: filters.near_location,
        radius_km: filters.radius_km,
      })
      .then((res) => {
        setApporteurMarkers(res.markers);
        setApporteurCenter(res.geocodedCenter);
      })
      .catch(() => {
        setApporteurMarkers([]);
        setApporteurCenter(null);
      });
  }, [viewMode, token, filters.near_location, filters.radius_km]);

  // Centroid de la zone géocodée : prend le 1er non-null entre prospects et
  // apporteurs (les 2 résolvent la même adresse, valeur identique). Sert au
  // zoom rayon dans FitBoundsToMarkers.
  const searchCenter = data?.geocodedCenter ?? apporteurCenter;

  // Signature sérialisée des coordonnées + filtre rayon - évite de
  // recalculer `FitBounds` à chaque render. Inclut le centroid de
  // recherche pour redéclencher le zoom quand l'utilisateur applique
  // un nouveau filtre rayon (sans changer les markers déjà chargés).
  const boundsKey = useMemo(() => {
    const radiusKey =
      searchCenter && filters.radius_km
        ? `radius:${searchCenter.latitude.toFixed(3)},${searchCenter.longitude.toFixed(3)},${filters.radius_km}`
        : '';
    const markersKey = data
      ? data.markers
          .map((m) => `${m.id}:${m.latitude.toFixed(3)},${m.longitude.toFixed(3)}`)
          .join('|')
      : '';
    return `${radiusKey}#${markersKey}`;
  }, [data, searchCenter, filters.radius_km]);

  return (
    <AppLayout fullBleed>
      <div className="absolute inset-0 flex">
        <aside className="z-[400] w-72 shrink-0 overflow-y-auto border-r border-border-subtle bg-surface/95 p-4 backdrop-blur-sm">
          <h1 className="text-lg font-semibold text-brand">Carte</h1>
          <p className="mt-0.5 text-xs text-text-muted">
            Prospects et apporteurs géolocalisés. Filtres en temps réel.
          </p>

          {/* Toggle 3 états Stripe-like (cf décision produit 2026-04-27).
              Default "prospects". Quand "apporteurs" ou "both", les filtres
              status/potentiel/contract_type sont sans effet sur les
              apporteurs (qui ont leur propre modèle de données). Le rayon
              géographique s'applique aux 2 catégories. */}
          <div className="mt-4">
            <ViewModeToggle value={viewMode} onChange={setViewMode} />
          </div>

          {/* Filtre rayon géographique (pattern identique à la page
              Prospects). Couple ville+km indissociable - le bouton
              « Appliquer » est désactivé tant que les 2 ne sont pas
              renseignés. « Effacer » remet à zéro. */}
          <div className="mt-4 rounded-md border border-border-subtle bg-white p-3">
            <Label htmlFor="map-near">Rayon géographique</Label>
            <div className="mt-1.5 space-y-2">
              <Input
                id="map-near"
                placeholder="Ville ou code postal"
                value={nearLocationInput}
                onChange={(e) => setNearLocationInput(e.target.value)}
              />
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="1"
                  max="2000"
                  placeholder="Rayon"
                  value={radiusKmInput}
                  onChange={(e) => setRadiusKmInput(e.target.value)}
                  className="flex-1"
                />
                <span className="text-xs text-text-muted">km</span>
              </div>
              <div className="flex gap-1.5">
                <Button
                  variant="primary"
                  size="sm"
                  className="flex-1"
                  disabled={
                    nearLocationInput.trim().length < 2 ||
                    !radiusKmInput ||
                    Number(radiusKmInput) <= 0
                  }
                  onClick={() => {
                    setFilters((f) => ({
                      ...f,
                      near_location: nearLocationInput.trim(),
                      radius_km: Number(radiusKmInput),
                    }));
                  }}
                >
                  Appliquer
                </Button>
                {(filters.near_location || filters.radius_km) && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setNearLocationInput('');
                      setRadiusKmInput('');
                      setFilters((f) => {
                        const { near_location: _nl, radius_km: _rk, ...rest } = f;
                        void _nl;
                        void _rk;
                        return rest;
                      });
                    }}
                  >
                    Effacer
                  </Button>
                )}
              </div>
              {filters.near_location && filters.radius_km && (
                <p className="text-[11px] text-text-muted">
                  Actif : {filters.radius_km} km autour de « {filters.near_location} »
                </p>
              )}
            </div>
          </div>

          {/* Filtres prospects (multi-dropdowns chips). Sans effet sur les
              apporteurs - mais les apporteurs n'ont pas de status/potential
              donc cohérent. */}
          {viewMode !== 'apporteurs' && (
            <div className="mt-4 space-y-4 text-sm">
              <div>
                <Label>Statut</Label>
                <div className="mt-1.5">
                  <MultiSelectChips
                    options={[
                      { value: 'cold', label: 'Froid' },
                      { value: 'warm', label: 'Chaud' },
                      { value: 'client', label: 'Client' },
                    ]}
                    selected={filters.status ?? []}
                    onChange={(next) =>
                      setFilters((f) => ({
                        ...f,
                        status: next.length === 0 ? undefined : (next as MapFilters['status']),
                      }))
                    }
                  />
                </div>
              </div>
              <div>
                <Label>Potentiel</Label>
                <div className="mt-1.5">
                  <MultiSelectChips
                    options={[
                      { value: 'low', label: 'Faible' },
                      { value: 'medium', label: 'Moyen' },
                      { value: 'high', label: 'Fort' },
                      { value: 'very_high', label: 'Très fort' },
                    ]}
                    selected={filters.potential ?? []}
                    onChange={(next) =>
                      setFilters((f) => ({
                        ...f,
                        potential:
                          next.length === 0 ? undefined : (next as MapFilters['potential']),
                      }))
                    }
                  />
                </div>
              </div>
              <div>
                <Label>Type de contrat</Label>
                <div className="mt-1.5">
                  <MultiSelectChips
                    options={contractTypes.map((c) => ({ value: c.id, label: c.code }))}
                    selected={filters.contract_type_id ?? []}
                    onChange={(next) =>
                      setFilters((f) => ({
                        ...f,
                        contract_type_id: next.length === 0 ? undefined : next,
                      }))
                    }
                  />
                </div>
              </div>
            </div>
          )}

          <div className="mt-4">
            <Button
              variant="secondary"
              size="sm"
              className="w-full"
              onClick={() => {
                setFilters({});
                setNearLocationInput('');
                setRadiusKmInput('');
              }}
              disabled={Object.keys(filters).length === 0}
            >
              Réinitialiser les filtres
            </Button>
          </div>

          {error && (
            <div className="mt-4">
              <Banner tone="danger">{error}</Banner>
            </div>
          )}

          <div className="mt-4 rounded-md border border-border-subtle bg-gray-50 p-3 text-xs">
            {viewMode !== 'apporteurs' && (
              <div className="flex items-center justify-between">
                <span className="text-text-muted">Prospects</span>
                <span className="font-medium text-brand">
                  {loading ? '…' : (data?.markers.length ?? 0)} / {data?.totalCount ?? 0}
                </span>
              </div>
            )}
            {viewMode !== 'prospects' && (
              <div className="flex items-center justify-between">
                <span className="text-text-muted">Apporteurs</span>
                <span className="font-medium text-brand">{apporteurMarkers.length}</span>
              </div>
            )}
            {data?.truncated && viewMode !== 'apporteurs' && (
              <div className="mt-2 text-warning-600">
                Plus de 1000 prospects - affinez les filtres.
              </div>
            )}
          </div>

          <Legend />
        </aside>

        <div className="relative flex-1">
          <MapContainer
            center={FRANCE_CENTER}
            zoom={FRANCE_ZOOM}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {viewMode !== 'apporteurs' &&
              data?.markers.map((m) => (
                <Marker key={m.id} position={[m.latitude, m.longitude]} icon={statusIcon(m)}>
                  <Popup>
                    <ProspectPopup marker={m} />
                  </Popup>
                </Marker>
              ))}
            {viewMode !== 'prospects' &&
              apporteurMarkers.map((a) => (
                <Marker
                  key={`app-${a.id}`}
                  position={[a.latitude, a.longitude]}
                  icon={apporteurIcon()}
                >
                  <Popup>
                    <ApporteurPopup marker={a} />
                  </Popup>
                </Marker>
              ))}
            <FitBoundsToMarkers
              markers={data?.markers ?? []}
              boundsKey={boundsKey}
              searchCenter={searchCenter}
              searchRadiusKm={filters.radius_km}
            />
          </MapContainer>
        </div>
      </div>
    </AppLayout>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * Toggle 3 états Stripe segmented (Prospects / Apporteurs / Les deux).
 * Pattern identique à `DateTypeToggle` du module kpi-consolides mais
 * avec 3 boutons au lieu de 2. Décision produit 2026-04-27.
 */
function ViewModeToggle({ value, onChange }: { value: ViewMode; onChange: (v: ViewMode) => void }) {
  const options: ReadonlyArray<{ value: ViewMode; label: string }> = [
    { value: 'prospects', label: 'Prospects' },
    { value: 'apporteurs', label: 'Apporteurs' },
    { value: 'both', label: 'Les deux' },
  ];
  return (
    <div className="inline-flex w-full items-center rounded-md border border-border-subtle bg-white text-xs">
      {options.map((opt, i) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={cn(
            'flex-1 px-2.5 py-1.5 font-medium transition-colors',
            i === 0 && 'rounded-l-md',
            i === options.length - 1 && 'rounded-r-md',
            value === opt.value ? 'bg-accent text-white' : 'text-text-muted hover:bg-gray-50',
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function Legend() {
  return (
    <div className="mt-4 rounded-md border border-border-subtle bg-white p-3 text-xs">
      <div className="mb-2 font-medium text-brand">Légende</div>
      <div className="space-y-1.5">
        {(Object.keys(STATUS_COLORS) as MapMarker['status'][]).map((s) => (
          <div key={s} className="flex items-center gap-2">
            <span
              aria-hidden
              className="inline-block h-3 w-3 rounded-full border border-white shadow"
              style={{ background: STATUS_COLORS[s] }}
            />
            <span className="text-text-muted">{STATUS_LABELS[s]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ApporteurPopup({ marker }: { marker: ApporteurMapMarker }) {
  // Refonte 2026-05-22 : un apporteur peut être une personne ou une
  // entreprise. `displayName` est calculé côté serveur. La sous-ligne
  // « employeur » n'a de sens que pour les personnes.
  return (
    <div className="min-w-[220px]">
      <div className="flex items-center gap-2">
        <span className="font-semibold text-brand">{marker.displayName}</span>
        <Badge tone="warning">
          {marker.type === 'company' ? 'Apporteur (entreprise)' : 'Apporteur'}
        </Badge>
      </div>
      {marker.type === 'person' && marker.company && (
        <div className="mt-0.5 text-xs text-text-muted">{marker.company}</div>
      )}
      <div className="mt-1 text-xs text-text-muted">
        {[marker.street, marker.postalCode, marker.city].filter(Boolean).join(' · ') ||
          'Adresse non renseignée'}
      </div>
      {(marker.email || marker.phone) && (
        <div className="mt-1 text-xs text-text-muted">
          {[marker.phone, marker.email].filter(Boolean).join(' · ')}
        </div>
      )}
      <div className="mt-1 text-xs text-text-muted">
        Opportunités liées : {marker.opportunitiesCount}
      </div>
      <div className="mt-2">
        <Link
          to={`/pipeline?view=list&apporteur_id=${marker.id}`}
          className="inline-flex items-center justify-center rounded-md bg-accent px-3 py-1 text-xs font-medium text-white hover:bg-accent-hover"
        >
          Voir les opps liées
        </Link>
      </div>
    </div>
  );
}

function ProspectPopup({ marker }: { marker: MapMarker }) {
  return (
    <div className="min-w-[220px]">
      <div className="flex items-center gap-2">
        <span className="font-semibold text-brand">{marker.displayName}</span>
        <Badge
          tone={
            marker.status === 'client'
              ? 'success'
              : marker.status === 'warm'
                ? 'warning'
                : 'neutral'
          }
        >
          {STATUS_LABELS[marker.status]}
        </Badge>
      </div>
      <div className="mt-1 text-xs text-text-muted">
        {[marker.addressStreet, marker.addressPostalCode, marker.addressCity]
          .filter(Boolean)
          .join(' · ') || 'Adresse non renseignée'}
      </div>
      <div className="mt-1 text-xs text-text-muted">
        Commercial : {marker.assignedUser.firstName} {marker.assignedUser.lastName}
      </div>
      <div className="mt-1 text-xs text-text-muted">Opportunités : {marker.opportunitiesCount}</div>
      <div className="mt-2">
        <Link
          to={`/prospects/${marker.id}`}
          className="inline-flex items-center justify-center rounded-md bg-accent px-3 py-1 text-xs font-medium text-white hover:bg-accent-hover"
        >
          Voir la fiche
        </Link>
      </div>
    </div>
  );
}

/**
 * Helper interne : recadre la vue de la carte. 2 modes :
 *  - **Filtre rayon actif** (centroid + rayon fournis) → zoome sur le
 *    cercle de recherche, indépendamment des markers. Permet à l'utilisateur
 *    de visualiser la zone scoped même quand il n'y a aucun marker dedans.
 *  - **Pas de filtre rayon** → fitBounds sur les markers affichés (fallback
 *    historique). Ignoré si 0 marker (on reste sur le centroid France).
 *
 * 1° latitude ≈ 111 km. Pour un rayon de R km, on étend les bounds de
 * R/111 degrés autour du centroid.
 */
function FitBoundsToMarkers({
  markers,
  boundsKey,
  searchCenter,
  searchRadiusKm,
}: {
  markers: MapMarker[];
  boundsKey: string;
  searchCenter?: { latitude: number; longitude: number } | null;
  searchRadiusKm?: number;
}) {
  const map = useMap();
  const lastKey = useRef<string>('');
  useEffect(() => {
    if (!boundsKey || boundsKey === lastKey.current) return;
    lastKey.current = boundsKey;
    // Mode 1 : filtre rayon - zoom sur le cercle de recherche.
    if (searchCenter && typeof searchRadiusKm === 'number' && searchRadiusKm > 0) {
      const deg = searchRadiusKm / 111;
      const bounds: LatLngBoundsExpression = [
        [searchCenter.latitude - deg, searchCenter.longitude - deg],
        [searchCenter.latitude + deg, searchCenter.longitude + deg],
      ];
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 13 });
      return;
    }
    // Mode 2 : pas de filtre rayon - fallback sur les markers.
    if (markers.length === 0) return;
    const bounds: LatLngBoundsExpression = markers.map(
      (m) => [m.latitude, m.longitude] as [number, number],
    );
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
  }, [boundsKey, markers, map, searchCenter, searchRadiusKm]);
  return null;
}
